import {
  Button,
  Toast,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { ApiError, SAVE_REFUSED, saveFile } from '@/domains/api';
import type { IListExportRequest } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { refusalMessage } from '../../refusal';

/**
 * The exports being written (SR-418) — *Export now…*'s files, on Lists and Reports, held outside the
 * dialog that asked for them, so a reader keeps working while a large one streams.
 *
 * **A module-level store, not a component's state**: the dialog is the report's or the list's, and
 * goes with its page, while the file is still on its way. `startExport` reads `POST /lists/export`
 * as a stream — its `X-Total-Count` at the first byte, the bytes counted since — and saves the file
 * itself when the body ends (`saveFile`, as the dialog did), whether or not anything still draws it.
 * There is no percentage to give: survey-api sends no length, so the rows, the megabytes received
 * and the time are said, under a bar that only moves.
 *
 * **The dialog draws its own export until it is let go** (*Continue in background*, or the dialog
 * closed by any other way than *Cancel*, which stops it); from then on the panel does —
 * `ExportDownloads`, the Team Portal shell's, bottom-right over every page: the same line, *Cancel*
 * (the fetch aborted), then the file's name once saved, which fades, or what went wrong, which
 * stays until dismissed. The kit's Radix `Toast` draws it rather than a fixed box of our own: it
 * brings the region, its F8 shortcut and the pause on hover. A sign-out takes the shell down, and
 * every export with it, so no file is saved for a session that has ended.
 */

export type ExportState = 'writing' | 'saved' | 'failed';

export interface IExport {
  id: number;
  state: ExportState;
  /** survey-api's name for the file from the first byte, the dialog's own before and without. */
  fileName: string;
  /** The first byte has come. */
  started: boolean;
  /** The rows the file holds (`X-Total-Count`); null before the first byte or without the header. */
  rows: Nullable<number>;
  received: number;
  startedAt: number;
  /** When it was saved or failed — the clock stops there. */
  endedAt: Nullable<number>;
  /** What a failure says. */
  said: Nullable<string>;
  /** Let go by its dialog: the panel draws it. */
  background: boolean;
}

/** How often the bytes received are passed on — a stream's chunks come hundreds a second. */
const PROGRESS_MS = 250;

/** How long a saved export's note stays, unless hovered. */
const SAVED_MS = 8_000;

let exports: readonly IExport[] = [];
const listeners = new Set<() => void>();
const controllers = new Map<number, AbortController>();
let nextId = 1;

function publish(next: readonly IExport[]) {
  exports = next;
  listeners.forEach((listener) => listener());
}

function update(id: number, patch: Partial<IExport>) {
  if (!exports.some((one) => one.id === id)) return;
  publish(exports.map((one) => (one.id === id ? { ...one, ...patch } : one)));
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Every export not yet dismissed, oldest first. */
export function useExports(): readonly IExport[] {
  return React.useSyncExternalStore(subscribe, () => exports);
}

/** One export by its id; null for none or one dismissed. */
export function useExport(id: Nullable<number>): Nullable<IExport> {
  const all = useExports();
  return id === null ? null : (all.find((one) => one.id === id) ?? null);
}

/** Starts an export; the file is saved under survey-api's name, else `fallbackName`. */
export function startExport(body: IListExportRequest, fallbackName: string): number {
  const id = nextId++;
  const controller = new AbortController();
  controllers.set(id, controller);
  publish([
    ...exports,
    {
      id,
      state: 'writing',
      fileName: fallbackName,
      started: false,
      rows: null,
      received: 0,
      startedAt: Date.now(),
      endedAt: null,
      said: null,
      background: false
    }
  ]);

  let received = 0;
  let pending: Nullable<number> = null;
  const flush = () => {
    if (pending !== null) window.clearTimeout(pending);
    pending = null;
    update(id, { received });
  };
  void listsApi
    .exportRows(body, {
      signal: controller.signal,
      onStart: ({ fileName, rows }) =>
        update(id, { started: true, rows, fileName: fileName ?? fallbackName }),
      onProgress: (bytes) => {
        received = bytes;
        pending ??= window.setTimeout(flush, PROGRESS_MS);
      }
    })
    .then(({ file, fileName }) => {
      if (pending !== null) window.clearTimeout(pending);
      const saved = saveFile(file, fileName ?? fallbackName);
      update(id, {
        state: saved ? 'saved' : 'failed',
        said: saved ? null : SAVE_REFUSED,
        received: file.size,
        endedAt: Date.now()
      });
    })
    .catch((error) => {
      flush();
      if (controller.signal.aborted) dismissExport(id);
      else {
        const started = exports.find((one) => one.id === id)?.started ?? false;
        update(id, { state: 'failed', said: exportRefusal(error, started), endedAt: Date.now() });
      }
    })
    .finally(() => controllers.delete(id));
  return id;
}

/** Stops an export still being written, and forgets it. */
export function cancelExport(id: number) {
  controllers.get(id)?.abort();
}

/** Hands an export to the panel; one already settled is forgotten, its dialog having said it. */
export function letGoExport(id: number) {
  const one = exports.find((each) => each.id === id);
  if (one?.state === 'writing') update(id, { background: true });
  else dismissExport(id);
}

export function dismissExport(id: number) {
  publish(exports.filter((one) => one.id !== id));
}

/** What a failed export says: survey-api's 400s by where they landed; a file cut once it had
 *  `started` (survey-api aborts the connection), in words rather than the browser's; else the
 *  refusal. */
function exportRefusal(error: unknown, started = false): string {
  const problems = error instanceof ApiError ? (error.problem?.problems ?? []) : [];
  if (problems.length > 0)
    return problems.map((problem) => `${problemWhere(problem.target)} ${problem.reason}`).join('; ');
  if (started && !(error instanceof ApiError)) return 'The file stopped part-way. Try again.';
  return refusalMessage(error, 'The file could not be written.');
}

/** Where a refusal landed, in words. */
function problemWhere(target: string): string {
  if (target === 'presetId') return 'The layout';
  if (target === 'format') return 'The format';
  if (target.startsWith('query.criteria')) return 'A condition of the list';
  if (target.startsWith('query.columns')) return 'A column of the list';
  if (target.startsWith('query.')) return 'The list';
  return 'The export';
}

/** The time now, a second at a time while `running`. */
function useClock(running: boolean): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [running]);
  return now;
}

/** "37,278 rows · 12.4 MB received · 0:07" while writing, "… 31.2 MB · 0:15" once saved. */
export function ExportLine({ export: one, className }: { export: IExport; className?: string }) {
  const writing = one.state === 'writing';
  const now = useClock(writing);
  const elapsed = elapsedWords((one.endedAt ?? now) - one.startedAt);
  const rows =
    one.rows === null ? null : `${one.rows.toLocaleString('en-US')} ${one.rows === 1 ? 'row' : 'rows'}`;
  const bytes = writing ? `${megabytes(one.received)} received` : megabytes(one.received);
  const parts = [rows, writing && !one.started ? 'Preparing the file' : bytes, elapsed];
  return (
    <span data-export-line="" className={className}>
      {parts.filter(Boolean).join(' · ')}
    </span>
  );
}

/** A bar that moves without saying how far: the file has no known length. */
export function ExportBar({ label }: { label: string }) {
  return (
    <div
      role="progressbar"
      aria-label={label}
      className="relative h-1 w-full overflow-hidden rounded-full bg-[var(--color-sapphire)]/15"
    >
      <span className="absolute inset-y-0 left-0 w-1/3 animate-[veritant-splash_1.1s_ease-in-out_infinite] rounded-full bg-[var(--color-sapphire)] motion-reduce:w-full motion-reduce:animate-none motion-reduce:opacity-40" />
    </div>
  );
}

function megabytes(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/** `m:ss` of a span in milliseconds. */
export function elapsedWords(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1_000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/** The exports let go by their dialogs, bottom-right over the Team Portal (module JSDoc). */
export function ExportDownloads() {
  const shown = useExports().filter((one) => one.background);
  // The shell going (a sign-out) stops what is still being written and forgets what settled, so
  // the next session in this tab starts with no note of the last one's files.
  React.useEffect(
    () => () => {
      controllers.forEach((controller) => controller.abort());
      publish([]);
    },
    []
  );

  return (
    <ToastProvider label="Downloads" swipeDirection="right">
      {shown.map((one) => (
        <ExportToast key={one.id} export={one} />
      ))}
      {/* Never in the way while empty: the kit's viewport is a padded fixed box, and only a toast
          takes the pointer. Bottom-right on a narrow screen too, clear of the header. */}
      <ToastViewport
        label="Downloads ({hotkey})"
        className="pointer-events-none top-auto right-0 bottom-0 flex-col"
      />
    </ToastProvider>
  );
}

function ExportToast({ export: one }: { export: IExport }) {
  const writing = one.state === 'writing';
  const title =
    one.state === 'saved'
      ? `Saved ${one.fileName}`
      : one.state === 'failed'
        ? 'Export failed'
        : `Exporting ${one.fileName}`;
  return (
    <Toast
      open
      type="background"
      duration={one.state === 'saved' ? SAVED_MS : Infinity}
      onOpenChange={(open) => {
        if (!open && !writing) dismissExport(one.id);
      }}
      onSwipeEnd={(event) => {
        // An export being written is stopped by Cancel alone: the swipe snaps back.
        if (!writing) return;
        event.preventDefault();
        event.currentTarget.setAttribute('data-swipe', 'cancel');
        event.currentTarget.style.removeProperty('--radix-toast-swipe-move-x');
      }}
      data-export={one.state}
      className="grid gap-2 space-x-0 p-4"
    >
      <div className="grid min-w-0 gap-1">
        <ToastTitle className="truncate">{title}</ToastTitle>
        {one.state === 'failed' ? (
          <ToastDescription className="text-destructive">{one.said}</ToastDescription>
        ) : (
          <ToastDescription className="text-muted-foreground tabular-nums">
            <ExportLine export={one} />
          </ToastDescription>
        )}
      </div>
      {writing && <ExportBar label={`Exporting ${one.fileName}`} />}
      <span className="sr-only" role="status">
        {writing ? '' : title}
      </span>
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => (writing ? cancelExport(one.id) : dismissExport(one.id))}
        >
          {writing ? 'Cancel' : 'Dismiss'}
        </Button>
      </div>
    </Toast>
  );
}
